/**
 * Containers API — mirrors `cargoContainers.js` mounted at /api/cargo/containers/*.
 *
 * IMPORTANT: types here are aligned 1-to-1 with the actual server response
 * shape (see `cargo_containers` table + `_publicContainerShape()` in
 * `services/cargoContainerService.js`). Field names match snake_case as
 * the server returns; do NOT introduce camelCase aliases here — the
 * Flutter model (`app/lib/core/models/container_model.dart`) uses the
 * same shape and is the source of truth.
 *
 * The case-conversion middleware on cargo-server intentionally does NOT
 * alter response bodies for /api/cargo/containers/* (the historical
 * Flutter clients depend on snake_case here).
 */
import { api } from './client';
import { unwrap } from './_unwrap';

// ─── Enums ────────────────────────────────────────────────────────────

/**
 * Server-side enum (cargo_containers.status). Surfaced verbatim so the
 * client can localize without ever guessing.
 */
export type ContainerStatus =
  | 'draft'
  | 'open_for_joins'
  | 'joins_closed'
  | 'consolidating'
  | 'sealed'
  | 'in_transit'
  | 'at_destination_port'
  | 'customs_filed'
  | 'customs_cleared'
  | 'unloading'
  | 'completed'
  | 'cancelled'
  | 'lost'
  | 'seized';

export type ContainerType = '20ft' | '40ft' | '40hc' | 'lcl' | 'air';

export type PricingMode = 'per_kg' | 'per_cbm' | 'mixed';
export type PricingMixedRule = 'max' | 'sum';

export type TransportMode =
  | 'sea_container'
  | 'air_freight'
  | 'road_truck'
  | 'road_reefer'
  | 'road_vehicle_carrier'
  | 'road_flatbed'
  | 'road_heavy_equipment'
  | 'road_tanker';

export type RecurrenceFrequency = 'weekly' | 'biweekly' | 'monthly' | 'bimonthly' | 'quarterly';

export type DeliveryMode = 'door' | 'pickup_at_warehouse';

/**
 * Server-side enum (cargo_container_participants.status). The flow is:
 * join_requested → approved → awaiting_supplier_dispatch → at_origin_warehouse
 *               → loaded_in_container → in_transit → at_destination_port
 *               → customs_filed_with_fee → customs_cleared
 *               → arrived_at_destination_warehouse → handed_to_final_mile
 *               → delivered.
 * Side branches: rejected, withdrawn, exception_terminal, refunded.
 */
export type ParticipantStatus =
  | 'join_requested'
  | 'approved'
  | 'awaiting_supplier_dispatch'
  | 'pending_warehouse_confirmation'
  | 'at_origin_warehouse'
  | 'loaded_in_container'
  | 'sealed'
  | 'in_transit'
  | 'at_destination_port'
  | 'customs_filed_with_fee'
  | 'customs_cleared'
  | 'arrived_at_destination_warehouse'
  | 'handed_to_final_mile'
  | 'delivered'
  | 'rejected'
  | 'withdrawn'
  | 'exception_pending'
  | 'exception_terminal'
  | 'refunded';

export type MilestoneType =
  | 'depart'
  | 'arrive_port'
  | 'customs_filed'
  | 'customs_duty'
  | 'customs_hold'
  | 'customs_released'
  | 'customs_seized'
  | 'loss_declared';


// ─── Container ────────────────────────────────────────────────────────

export interface CompanyBrand {
  id: number;
  name: string;
  name_ar?: string | null;
  public_code: string;
  logo_url?: string | null;
}

export interface Container {
  id: number;
  public_code: string;
  organizer_id: string;          // owner user_id (TEXT)
  title: string;
  description?: string | null;
  cover_image_url?: string | null;
  container_type: ContainerType;
  carrier?: string | null;
  carrier_subtype?: string | null;
  booking_ref?: string | null;
  bol_number?: string | null;
  seal_number?: string | null;
  origin_country: string;        // ISO2 lowercase
  origin_port_code?: string | null;
  origin_port_name?: string | null;
  origin_warehouse_id?: number | null;
  destination_country: string;   // ISO2 lowercase
  destination_port_code?: string | null;
  destination_port_name?: string | null;
  destination_center_id?: number | null;
  destination_center_name?: string | null;
  max_cbm?: number | null;
  max_weight_kg?: number | null;
  used_cbm?: number | null;
  used_weight_kg?: number | null;
  remaining_cbm_approx?: number | null;
  remaining_weight_kg_approx?: number | null;
  pricing_mode: PricingMode;
  price_per_kg_usd?: number | null;
  price_per_cbm_usd?: number | null;
  pricing_mixed_rule?: PricingMixedRule | null;
  minimum_charge_usd?: number | null;
  clearance_fee_usd?: number | null;
  insurance_rate_bps?: number | null;
  joins_open_at?: string | null;
  joins_close_at: string;
  warehouse_cutoff_at: string;
  estimated_departure_at?: string | null;
  actual_departure_at?: string | null;
  estimated_arrival_at?: string | null;
  actual_arrival_at?: string | null;
  estimated_clearance_at?: string | null;
  allowed_categories?: string[] | null;
  prohibited_categories?: string[] | null;
  max_piece_weight_kg?: number | null;
  requires_invoice?: boolean | null;
  requires_hs_code?: boolean | null;
  min_declared_value_usd?: number | null;
  max_declared_value_usd?: number | null;
  is_tentative?: boolean | null;
  tentative_note?: string | null;
  instructions_file_url?: string | null;
  instructions_file_name?: string | null;
  shipment_code?: string | null;
  status: ContainerStatus;
  transport_mode: TransportMode;
  total_slots?: number | null;
  price_per_vehicle_usd?: number | null;
  is_recurring?: boolean | null;
  recurrence_frequency?: RecurrenceFrequency | null;
  is_pool?: boolean | null;
  created_at: string;
  company_brand?: CompanyBrand | null;
}


// ─── Participant ──────────────────────────────────────────────────────

export interface ContainerParticipant {
  id: number;
  container_id: number;
  sender_id: string;
  public_code: string;
  title?: string | null;
  category?: string | null;
  hs_code?: string | null;
  pieces_count: number;
  est_weight_kg: number;
  est_cbm?: number | null;
  actual_weight_kg?: number | null;
  actual_cbm?: number | null;
  actual_pieces_count?: number | null;
  declared_value_usd: number;
  supplier_name?: string | null;
  supplier_phone?: string | null;
  supplier_invoice_url?: string | null;
  supplier_invoice_number?: string | null;
  supplier_photos_json?: string[] | null;
  destination_city_id?: number | null;
  destination_city_name?: string | null;
  destination_address_text: string;
  destination_contact_phone: string;
  destination_contact_name?: string | null;
  delivery_mode: DeliveryMode;
  linked_shipment_id?: number | null;
  price_shipping_usd?: number | null;
  price_clearance_usd?: number | null;
  price_insurance_usd?: number | null;
  price_final_mile_usd?: number | null;
  price_total_usd?: number | null;
  status: ParticipantStatus;
  rejection_reason?: string | null;
  cancel_reason?: string | null;
  exception_reason?: string | null;
  warehouse_arrived_at?: string | null;
  warehouse_received_by?: string | null;
  warehouse_confirmation_at?: string | null;
  warehouse_confirmation_note?: string | null;
  warehouse_notes?: string | null;
  intake_photos_json?: string[] | null;
  weight_ticket_url?: string | null;
  port_release_photo_url?: string | null;
  insurance_opted_in?: boolean | null;
  insurance_certificate_url?: string | null;
  dispatch_proof_url?: string | null;
  dispatch_proof_photos_json?: string[] | null;
  dispatch_proof_uploaded_at?: string | null;
  dispatch_proof_note?: string | null;
  customs_fee_usd?: number | null;
  customs_receipt_url?: string | null;
  customs_fee_set_at?: string | null;
  customs_fee_paid_at?: string | null;
  package_barcode?: string | null;
  package_barcode_at?: string | null;
  created_at: string;
  updated_at?: string | null;
  // Denormalized fields when listed via /participants/my
  container_public_code?: string | null;
  container_title?: string | null;
  container_status?: ContainerStatus | null;
  origin_country?: string | null;
  origin_port_name?: string | null;
  destination_country?: string | null;
  destination_port_name?: string | null;
  estimated_arrival_at?: string | null;
  actual_arrival_at?: string | null;
}


// ─── Quote / Availability ─────────────────────────────────────────────

export interface ContainerQuote {
  shipping_usd: number;
  clearance_usd: number;
  insurance_usd: number;
  final_mile_usd: number;
  total_usd: number;
  currency: 'USD';
}


// ─── Create / Edit input types ────────────────────────────────────────

export interface CreateContainerInput {
  title: string;
  description?: string;
  coverImageUrl?: string;
  isTentative?: boolean;
  tentativeNote?: string;
  shipmentCode?: string;
  transportMode?: TransportMode;
  containerType: ContainerType;
  carrier?: string;
  carrierSubtype?: string;
  originCountry: string;          // ISO2 lowercase
  originPortCode?: string;
  originPortName?: string;
  originWarehouseId?: number;
  destinationCountry: string;
  destinationPortCode?: string;
  destinationPortName?: string;
  maxCbm?: number;
  maxWeightKg?: number;
  pricingMode: PricingMode;
  pricePerKgUsd?: number;
  pricePerCbmUsd?: number;
  pricingMixedRule?: PricingMixedRule;
  minimumChargeUsd?: number;
  clearanceFeeUsd?: number;
  insuranceRateBps?: number;
  joinsOpenAt?: string;
  joinsCloseAt: string;
  warehouseCutoffAt: string;
  estimatedDepartureAt?: string;
  estimatedArrivalAt?: string;
  estimatedClearanceAt?: string;
  allowedCategories?: string[];
  prohibitedCategories?: string[];
  maxPieceWeightKg?: number;
  requiresInvoice?: boolean;
  requiresHsCode?: boolean;
  minDeclaredValueUsd?: number;
  maxDeclaredValueUsd?: number;
  isRecurring?: boolean;
  recurrenceFrequency?: RecurrenceFrequency;
  notesInternal?: string;
  // Per-vehicle modes (optional, validated by transport_mode):
  totalSlots?: number;
  pricePerVehicleUsd?: number;
}

export interface JoinContainerInput {
  title?: string;
  category: string;                // required, lowercase
  hsCode?: string;
  piecesCount?: number;            // default 1 server-side
  estWeightKg: number;
  estCbm?: number;
  declaredValueUsd: number;
  supplierName?: string;
  supplierPhone?: string;
  supplierInvoiceUrl?: string;
  supplierInvoiceNumber?: string;
  supplierPhotos?: string[];
  destinationCityId?: number;
  destinationCityName: string;     // required
  destinationAddressText: string;  // required, min 3 chars
  destinationContactPhone: string; // required, +XXX format
  destinationContactName: string;  // required, min 2 chars
  deliveryMode?: DeliveryMode;
  insuranceOptedIn?: boolean;
}


// ─── Container events / documents ─────────────────────────────────────

export interface ContainerEvent {
  id: number;
  container_id: number;
  type: string;
  note?: string | null;
  photo_url?: string | null;
  location_text?: string | null;
  metadata?: Record<string, unknown> | null;
  occurred_at: string;
  actor_user_id?: string | null;
  created_at: string;
}

export interface ContainerDocument {
  id: number;
  container_id: number;
  kind: string;
  file_url: string;
  file_name?: string | null;
  visible_to_senders?: boolean | null;
  notes?: string | null;
  uploaded_at: string;
  uploaded_by?: string | null;
}

export interface OriginWarehouse {
  id: number;
  organizer_id?: string | null;
  company_id?: number | null;
  country: string;
  city: string;
  name: string;
  address_latin: string;
  address_local?: string | null;
  contact_name?: string | null;
  contact_phone?: string | null;
  operating_hours?: Record<string, unknown> | null;
  status?: string | null;
  is_active?: boolean | null;
  shipment_code?: string | null;
  instructions_file_url?: string | null;
  instructions_file_name?: string | null;
  created_at: string;
}


// ─── API surface ──────────────────────────────────────────────────────

export const containersApi = {
  // Public marketplace
  async listOpen(params?: { originCountry?: string; destinationCountry?: string; transportMode?: TransportMode }): Promise<Container[]> {
    const r = await api.get('/api/cargo/containers/open', { params });
    return unwrap<Container[]>(r);
  },
  async findByCode(code: string): Promise<Container> {
    const r = await api.get(`/api/cargo/containers/by-code/${encodeURIComponent(code)}`);
    return unwrap<Container>(r);
  },
  async findById(id: number): Promise<Container> {
    const r = await api.get(`/api/cargo/containers/${id}`);
    return unwrap<Container>(r);
  },
  async availability(params?: { originCountry?: string; destinationCountry?: string; transportModes?: string; categoryKey?: string }): Promise<{
    by_mode: Array<{ transport_mode: TransportMode; open_count: number; next_departure_at?: string }>;
    total_open: number;
    next_departure_at?: string | null;
  }> {
    const r = await api.get('/api/cargo/containers/availability', { params });
    return unwrap(r);
  },

  // Sender / participant side
  async myParticipants(): Promise<ContainerParticipant[]> {
    const r = await api.get('/api/cargo/containers/participants/my');
    return unwrap<ContainerParticipant[]>(r);
  },
  async findParticipant(pid: number): Promise<ContainerParticipant> {
    const r = await api.get(`/api/cargo/containers/participants/${pid}`);
    return unwrap<ContainerParticipant>(r);
  },
  async getQuote(containerId: number, payload: {
    estWeightKg: number; estCbm?: number; declaredValueUsd: number;
    insuranceOptedIn?: boolean; deliveryMode?: DeliveryMode; destinationCityId?: number;
  }): Promise<ContainerQuote> {
    const r = await api.post(`/api/cargo/containers/${containerId}/quote`, payload);
    return unwrap<ContainerQuote>(r);
  },
  async join(containerId: number, payload: JoinContainerInput): Promise<ContainerParticipant> {
    const r = await api.post(`/api/cargo/containers/${containerId}/participants`, payload);
    return unwrap<ContainerParticipant>(r);
  },
  async uploadDispatchProof(pid: number, payload: { photos: string[]; mainPhotoUrl?: string; note?: string }): Promise<void> {
    await api.post(`/api/cargo/containers/participants/${pid}/dispatch-proof`, payload);
  },
  async withdrawParticipant(pid: number, reason?: string): Promise<void> {
    await api.post(`/api/cargo/containers/participants/${pid}/withdraw`, { reason });
  },

  // Organizer side
  async listMyContainers(): Promise<Container[]> {
    const r = await api.get('/api/cargo/containers/my');
    return unwrap<Container[]>(r);
  },
  async listParticipants(containerId: number): Promise<ContainerParticipant[]> {
    const r = await api.get(`/api/cargo/containers/${containerId}/participants`);
    return unwrap<ContainerParticipant[]>(r);
  },
  async approveParticipant(containerId: number, pid: number, opts?: { quotedPriceUsd?: number }): Promise<void> {
    await api.post(`/api/cargo/containers/${containerId}/participants/${pid}/approve`, opts || {});
  },
  async rejectParticipant(containerId: number, pid: number, reason: string): Promise<void> {
    await api.post(`/api/cargo/containers/${containerId}/participants/${pid}/reject`, { reason });
  },
  async confirmWarehouse(containerId: number, pid: number, payload: {
    actualWeightKg?: number; actualCbm?: number; actualPiecesCount?: number;
    photos?: string[]; weightTicketUrl?: string; note?: string;
  }): Promise<void> {
    await api.post(`/api/cargo/containers/${containerId}/participants/${pid}/confirm-warehouse`, payload);
  },
  async rejectWarehouse(containerId: number, pid: number, payload: { reason: string; photos?: string[] }): Promise<void> {
    await api.post(`/api/cargo/containers/${containerId}/participants/${pid}/reject-warehouse`, payload);
  },
  async setCustomsFee(containerId: number, pid: number, payload: { feeUsd?: number; receiptUrl?: string }): Promise<void> {
    await api.post(`/api/cargo/containers/${containerId}/participants/${pid}/customs-fee`, payload);
  },

  // Lifecycle
  async create(input: CreateContainerInput): Promise<Container> {
    const r = await api.post('/api/cargo/containers', input);
    return unwrap<Container>(r);
  },
  async edit(id: number, patch: Partial<CreateContainerInput>): Promise<Container> {
    const r = await api.put(`/api/cargo/containers/${id}`, patch);
    return unwrap<Container>(r);
  },
  async publish(id: number): Promise<Container> {
    const r = await api.post(`/api/cargo/containers/${id}/publish`);
    return unwrap<Container>(r);
  },
  async closeJoins(id: number): Promise<Container> {
    const r = await api.post(`/api/cargo/containers/${id}/close-joins`);
    return unwrap<Container>(r);
  },
  async seal(id: number, payload: { sealNumber: string; bookingRef: string; bolNumber: string; bolPdfUrl?: string; commercialInvoiceUrl?: string; packingListUrl?: string; carrier?: string }): Promise<Container> {
    const r = await api.post(`/api/cargo/containers/${id}/seal`, payload);
    return unwrap<Container>(r);
  },
  async addMilestone(id: number, payload: { type: MilestoneType; note?: string; photoUrl?: string; locationText?: string; metadata?: Record<string, unknown>; extraFields?: Record<string, unknown> }): Promise<void> {
    await api.post(`/api/cargo/containers/${id}/milestones`, payload);
  },
  async cancel(id: number, reason?: string): Promise<Container> {
    const r = await api.post(`/api/cargo/containers/${id}/cancel`, { reason });
    return unwrap<Container>(r);
  },

  // Events / Documents
  async listEvents(id: number): Promise<ContainerEvent[]> {
    const r = await api.get(`/api/cargo/containers/${id}/events`);
    return unwrap<ContainerEvent[]>(r);
  },
  async listDocuments(id: number): Promise<ContainerDocument[]> {
    const r = await api.get(`/api/cargo/containers/${id}/documents`);
    return unwrap<ContainerDocument[]>(r);
  },
  async addDocument(id: number, payload: { kind: string; fileUrl: string; fileName?: string; visibleToSenders?: boolean; notes?: string }): Promise<ContainerDocument> {
    const r = await api.post(`/api/cargo/containers/${id}/documents`, payload);
    return unwrap<ContainerDocument>(r);
  },

  // Origin warehouses (organizer-side)
  async listMyOriginWarehouses(): Promise<OriginWarehouse[]> {
    const r = await api.get('/api/cargo/origin-warehouses?owned=true');
    return unwrap<OriginWarehouse[]>(r);
  },
  async createOriginWarehouse(input: {
    country: string; city: string; name: string;
    addressLatin: string; addressLocal?: string;
    contactName?: string; contactPhone?: string;
    operatingHours?: Record<string, unknown>;
    instructionsFileUrl?: string; instructionsFileName?: string;
    shipmentCode?: string;
  }): Promise<OriginWarehouse> {
    const r = await api.post('/api/cargo/origin-warehouses', input);
    return unwrap<OriginWarehouse>(r);
  },
};
